import React, { useState, useEffect, useRef, useCallback } from "react";
import VirtualKeyboard from "./VirtualKeyboard";
import SearchBox from "./SearchBox";
import { useTranslation } from "react-i18next";

interface SearchWithVirtualkeyboardProps {
  value: string;
  onChange: (v: string) => void;
  onFocus?: () => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  onSelect?: (start: number, end: number) => void;
  resetKey?: number;
  resultCount?: number; // 現在の検索条件での該当件数 (キーボード上に表示)
}
// 濁点・半濁点の変換マップ
export const dakutenMap: Record<string, string> = {
  // k -> g
  か: "が",
  き: "ぎ",
  く: "ぐ",
  け: "げ",
  こ: "ご",
  // s -> z
  さ: "ざ",
  し: "じ",
  す: "ず",
  せ: "ぜ",
  そ: "ぞ",
  // t -> d
  た: "だ",
  ち: "ぢ",
  つ: "づ",
  て: "で",
  と: "ど",
  // h -> b
  は: "ば",
  ひ: "び",
  ふ: "ぶ",
  へ: "べ",
  ほ: "ぼ",
  // small kana variants sometimes used
  う: "ゔ",
};

export const handakutenMap: Record<string, string> = {
  // h -> p
  は: "ぱ",
  ひ: "ぴ",
  ふ: "ぷ",
  へ: "ぺ",
  ほ: "ぽ",
};

const SearchWithVirtualkeyboard: React.FC<SearchWithVirtualkeyboardProps> = ({
  value,
  onChange,
  inputRef,
  onSelect,
  resetKey,
  resultCount,
}) => {
  const desktopMediaQuery = "(min-width: 640px)";
  const [caretIndex, setCaretIndex] = useState(0);
  const [search, setSearch] = useState("");
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const keyboardRef = useRef<HTMLDivElement | null>(null);
  const internalInputRef = useRef<HTMLInputElement | null>(null);
  const searchInputRef = inputRef ?? internalInputRef;
  // 翻訳インスタンスのインポート
  const { t } = useTranslation();

  const applyDiacritic = (char: string, mark: string): string | null => {
    if (mark === "゛") {
      return dakutenMap[char] ?? null;
    }
    if (mark === "゜") {
      return handakutenMap[char] ?? null;
    }
    return null;
  };

  const handleKeyPress = (key: string) => {
    const input = searchInputRef.current;
    let start = caretIndex;
    let end = caretIndex;
    if (input) {
      start = input.selectionStart ?? caretIndex;
      end = input.selectionEnd ?? caretIndex;
    }

    // 濁点・半濁点処理: 直前の1文字が変換対象なら置換する
    if (key === "゛" || key === "゜") {
      const cur = search;
      const targetIndex = start > 0 ? start - 1 : -1;
      if (targetIndex >= 0) {
        const prevChar = cur[targetIndex];
        const replaced = applyDiacritic(prevChar, key);
        if (replaced) {
          const newSearch = cur.slice(0, targetIndex) + replaced + cur.slice(targetIndex + 1);
          setSearch(newSearch);
          onChange(newSearch);
          // キャレットは置換後の文字の後ろに移動
          setCaretIndex(targetIndex + 1);
          if (onSelect) onSelect(targetIndex + 1, targetIndex + 1);
          return;
        }
      }
      // 変換対象でないなら入力しない
      return;
    }

    const before = search.slice(0, start);
    const after = search.slice(end);
    const next = before + key + after;
    setSearch(next);
    onChange(next);
    // move caret after inserted text
    setCaretIndex(start + key.length);
    if (onSelect) onSelect(start + key.length, start + key.length);
  };

  const blurInput = useCallback(() => {
    const el = searchInputRef.current;
    if (el && typeof el.blur === "function") {
      el.blur();
      return;
    }
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, [searchInputRef]);

  const handleBackspace = () => {
    const input = searchInputRef.current;
    let start = caretIndex;
    let end = caretIndex;
    if (input) {
      start = input.selectionStart ?? caretIndex;
      end = input.selectionEnd ?? caretIndex;
    }

    let newSearch = search;
    if (start !== end) {
      // delete selection
      newSearch = search.slice(0, start) + search.slice(end);
      setSearch(newSearch);
      setCaretIndex(start);
    } else if (start > 0) {
      newSearch = search.slice(0, start - 1) + search.slice(start);
      setSearch(newSearch);
      setCaretIndex(start - 1);
    }

    // notify parent of new value
    onChange(newSearch);
    if (onSelect) {
      const pos = Math.min(Math.max(caretIndex, 0), newSearch.length);
      onSelect(pos, pos);
    }
  };

  const handleClear = () => {
    setSearch("");
    setCaretIndex(0);
    onChange("");
    if (onSelect) onSelect(0, 0);
  };

  const handleClose = () => {
    blurInput();
    setShowKeyboard(false);
  };

  // キオスクリセット時にキーボードを閉じてキャレットをリセット
  useEffect(() => {
    if (!resetKey) return;
    blurInput();
    setShowKeyboard(false);
    setCaretIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  useEffect(() => {
    const mediaQuery = window.matchMedia(desktopMediaQuery);
    const handleMediaQueryChange = (event: MediaQueryListEvent) => {
      setIsDesktop(event.matches);
    };

    setIsDesktop(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleMediaQueryChange);

    return () => {
      mediaQuery.removeEventListener("change", handleMediaQueryChange);
    };
  }, [desktopMediaQuery]);

  useEffect(() => {
    if (isDesktop) return;
    blurInput();
    setShowKeyboard(false);
  }, [isDesktop, blurInput]);

  const handleRight = () => {
    const input = searchInputRef.current;
    let pos = caretIndex;
    if (input) {
      const selectionEnd = input.selectionEnd ?? caretIndex;
      const selectionStart = input.selectionStart ?? caretIndex;
      if (selectionEnd !== selectionStart) {
        pos = selectionEnd;
      } else {
        pos = Math.min(selectionEnd + 1, search.length);
      }
    } else {
      pos = Math.min(caretIndex + 1, search.length);
    }
    setCaretIndex(pos);
    if (onSelect) onSelect(pos, pos);
  };

  const handleLeft = () => {
    const input = searchInputRef.current;
    let pos = caretIndex;
    if (input) {
      const selectionStart = input.selectionStart ?? caretIndex;
      const selectionEnd = input.selectionEnd ?? caretIndex;
      if (selectionEnd !== selectionStart) {
        pos = selectionStart;
      } else {
        pos = Math.max(selectionStart - 1, 0);
      }
    } else {
      pos = Math.max(caretIndex - 1, 0);
    }
    setCaretIndex(pos);
    if (onSelect) onSelect(pos, pos);
  };

  // input の selection を反映させる（search や caret が更新されたら input に反映）
  useEffect(() => {
    const el = searchInputRef.current;
    if (!el) return;
    if (isComposing) return;
    const pos = Math.min(Math.max(caretIndex, 0), search.length);
    if (showKeyboard || document.activeElement === el) {
      el.focus();
    }
    el.setSelectionRange(pos, pos);
  }, [caretIndex, isComposing, search, showKeyboard, searchInputRef]);

  // sync with parent value
  useEffect(() => {
    setSearch(value ?? "");
  }, [value]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.target.value;
    setSearch(nextValue);
    onChange(nextValue);
    if (isComposing) return;
    const pos = e.target.selectionStart ?? nextValue.length;
    setCaretIndex(pos);
    if (onSelect) onSelect(pos, pos);
  };

  const handleSelection = (start: number, end: number) => {
    if (isComposing) return;
    // selection を親で同期しておく
    setCaretIndex(end ?? start ?? 0);
    if (onSelect) onSelect(start ?? 0, end ?? start ?? 0);
  };

  const handleCompositionStart = () => {
    setIsComposing(true);
  };

  const handleCompositionEnd = (e: React.CompositionEvent<HTMLInputElement>) => {
    const nextValue = e.currentTarget.value;
    const pos = e.currentTarget.selectionStart ?? nextValue.length;
    setIsComposing(false);
    setSearch(nextValue);
    onChange(nextValue);
    setCaretIndex(pos);
    if (onSelect) onSelect(pos, pos);
  };

  const handleBlur = () => {
    setIsComposing(false);
  };

  // コンテナ外をクリック、ドラッグした時キーボードを閉じる
  React.useEffect(() => {
    const handlePointerOrMouseOrTouch = (event: Event) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (containerRef.current && containerRef.current.contains(target)) return;
      if (keyboardRef.current && keyboardRef.current.contains(target)) return;
      blurInput();
      setShowKeyboard(false);
    };

    const handleDragStart = () => {
      blurInput();
      setShowKeyboard(false);
    };

    if (showKeyboard) {
      document.addEventListener("pointerdown", handlePointerOrMouseOrTouch);
      document.addEventListener("mousedown", handlePointerOrMouseOrTouch);
      document.addEventListener("touchstart", handlePointerOrMouseOrTouch);
      document.addEventListener("dragstart", handleDragStart);
    }

    return () => {
      document.removeEventListener("pointerdown", handlePointerOrMouseOrTouch);
      document.removeEventListener("mousedown", handlePointerOrMouseOrTouch);
      document.removeEventListener("touchstart", handlePointerOrMouseOrTouch);
      document.removeEventListener("dragstart", handleDragStart);
    };
  }, [showKeyboard, blurInput]);
  return (
    <div ref={containerRef} className="w-full flex flex-col gap-2">
      <SearchBox
        placeholder={t("例： いとう ・ C&D ・ 363")}
        value={search}
        onChange={handleSearchChange}
        onFocus={() => {
          if (isDesktop) {
            setShowKeyboard(true);
          }
        }}
        onBlur={handleBlur}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
        inputRef={searchInputRef}
        onSelect={handleSelection}
      />
      {showKeyboard && isDesktop && (
        <>
          <div
            ref={keyboardRef}
            className="fixed bottom-4 left-[32rem] right-4 z-100 flex items-end justify-center"
            aria-hidden={!showKeyboard}
          >
            <div className="w-full">
              <VirtualKeyboard
                onKeyPress={handleKeyPress}
                onBackspace={handleBackspace}
                onClear={handleClear}
                onClose={handleClose}
                onRight={handleRight}
                onLeft={handleLeft}
                value={search}
                caretIndex={caretIndex}
                placeholder={t("例： いとう ・ C&D ・ 363")}
                resultCount={resultCount}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SearchWithVirtualkeyboard;
